import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  CopyButton,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconBook2, IconCheck, IconCopy } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useBlocker, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { toTblHomeInput, useHomeBlockStore } from "../../store/useHomeBlockStore";
import EditBanner from "./editors/EditBanner";
import EditBusiness from "./editors/EditBusiness";
import EditCatGrid from "./editors/EditCatGrid";
import EditGallery from "./editors/EditGallery";
import EditHero from "./editors/EditHero";
import EditNews from "./editors/EditNews";
import EditNumbers from "./editors/EditNumbers";
import EditPersona from "./editors/EditPersona";
import EditProduct from "./editors/EditProduct";
import EditPromo from "./editors/EditPromo";
import EditService from "./editors/EditService";
import EditTiles from "./editors/EditTiles";
import EditVideo from "./editors/EditVideo";
import { GUIDE_PATH } from "./HomeBlockGuidePage";
import { DisplaySettings, SectionCard, ZoomableImage } from "./HomeBlockShared";
import { ACCENT, HomeBlock, templateInfo } from "./homeBlockData";
import classes from "./homeBlock.module.css";

const BlockEditor = ({ block }: { block: HomeBlock }) => {
  switch (block.template) {
    case "hero":
      return <EditHero block={block} />;
    case "product":
      return <EditProduct key={block.id} block={block} />;
    case "tiles":
      return <EditTiles key={block.id} block={block} />;
    case "catgrid":
      return <EditCatGrid key={block.id} block={block} />;
    case "persona":
      return <EditPersona key={block.id} block={block} />;
    case "banner":
      return <EditBanner key={block.id} block={block} />;
    case "business":
      return <EditBusiness key={block.id} block={block} />;
    case "service":
      return <EditService key={block.id} block={block} />;
    case "numbers":
      return <EditNumbers key={block.id} block={block} />;
    case "news":
      return <EditNews key={block.id} block={block} />;
    case "video":
      return <EditVideo key={block.id} block={block} />;
    case "gallery":
      return <EditGallery key={block.id} block={block} />;
    case "promo":
      return <EditPromo key={block.id} block={block} />;
    default:
      return (
        <SectionCard title="Nội dung">
          <Text fz={14} c="var(--hb-ink-2)">
            Mẫu {templateInfo(block.template).name} chưa có trình sửa nội dung.
            Xem danh sách trường cần nhập ở trang Mẫu block.
          </Text>
          <Anchor
            component={Link}
            to="/fixed-content/home-block/templates"
            fz={14}
            fw={600}
            c="var(--hb-accent)"
          >
            Mở trang Mẫu block
          </Anchor>
        </SectionCard>
      );
  }
};

// Đúng dữ liệu gửi API TblHome/edit: metadata là cột riêng, cấu hình chi tiết là chuỗi JSON ở cột Data
const SavedDataPanel = ({ block, order }: { block: HomeBlock; order: number }) => {
  const { data, ...metadata } = toTblHomeInput(block, order);
  const configJson = block.config === undefined ? null : JSON.stringify(block.config, null, 2);
  const hasBlobImage = !!configJson && configJson.includes('"blob:');

  return (
    <Stack gap={16}>
      <SectionCard
        title="Cột riêng (metadata)"
        right={
          <Text fz={13} className={classes.muted}>
            Gửi API TblHome/edit; ngày, người sửa do API tự gán
          </Text>
        }
      >
        <Code block className={classes.jsonBlock}>
          {JSON.stringify(metadata, null, 2)}
        </Code>
      </SectionCard>
      <SectionCard
        title="Cột Data (JSON)"
        right={
          configJson && (
            <CopyButton value={configJson}>
              {({ copied, copy }) => (
                <Button
                  size="xs"
                  variant="default"
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={copy}
                >
                  {copied ? "Đã chép" : "Chép JSON"}
                </Button>
              )}
            </CopyButton>
          )
        }
      >
        {configJson ? (
          <>
            <Text fz={13} className={classes.muted}>
              Gửi lên dạng chuỗi ({data.length.toLocaleString("vi-VN")} ký tự). Kiểu dữ liệu từng
              trường ở homeBlockConfig.ts. Danh mục, thương hiệu lưu id; sản phẩm
              lưu mã; icon lưu tên Lucide hoặc URL ảnh; ảnh lưu URL.
            </Text>
            {hasBlobImage && (
              <Alert color="orange" variant="light" radius="sm" p={10}>
                <Text fz={13}>
                  Ảnh vừa chọn đang là địa chỉ blob: tạm, chỉ để xem trước. Bấm Lưu block để tải ảnh
                  lên CDN, chỗ này sẽ đổi thành URL trên CDN.
                </Text>
              </Alert>
            )}
            <ScrollArea.Autosize mah={560} type="auto">
              <Code block className={classes.jsonBlock}>
                {configJson}
              </Code>
            </ScrollArea.Autosize>
          </>
        ) : (
          <Text fz={14} c="var(--hb-ink-2)">
            Chưa có cấu hình cho block {templateInfo(block.template).name}. Mở tab Nội dung để tạo
            cấu hình mặc định.
          </Text>
        )}
      </SectionCard>
    </Stack>
  );
};

const HomeBlockEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const blocks = useHomeBlockStore((s) => s.blocks);
  const status = useHomeBlockStore((s) => s.status);
  const load = useHomeBlockStore((s) => s.load);
  const saving = useHomeBlockStore((s) => s.saving);
  const saveBlock = useHomeBlockStore((s) => s.saveBlock);
  const revertBlock = useHomeBlockStore((s) => s.revertBlock);
  const unsaved = useHomeBlockStore((s) => !!id && s.dirtyIds.includes(id));
  const [tab, setTab] = useState<string | null>("content");
  const [leaving, setLeaving] = useState<"save" | "discard" | null>(null);

  // Rời màn (Huỷ thay đổi, Danh sách block, đổi menu) khi còn thay đổi chưa lưu: hỏi lưu hay bỏ
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      unsaved && currentLocation.pathname !== nextLocation.pathname
  );

  const leave = async (mode: "save" | "discard") => {
    if (!id) return;
    setLeaving(mode);
    let ok = true;
    if (mode === "save") {
      ok = await saveBlock(id);
      if (ok) NotificationExtension.Success("Đã lưu block");
    } else {
      await revertBlock(id);
    }
    setLeaving(null);
    // Lưu lỗi thì ở lại màn để sửa, thông báo lỗi đã hiện
    if (ok) blocker.proceed?.();
    else blocker.reset?.();
  };

  // Tải lại hoặc đóng tab: trình duyệt tự hỏi, vì ảnh mới chọn chỉ đang xem trước sẽ mất
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  // Mở thẳng link màn sửa thì danh sách chưa có: tải từ API
  useEffect(() => {
    load();
  }, [load]);

  const index = blocks.findIndex((b) => b.id === id);
  const block = index >= 0 ? blocks[index] : undefined;

  if (!block && (status === "idle" || status === "loading")) {
    return (
      <Box className={classes.page}>
        <BreadCrumb />
        <Group gap={10} mt={24}>
          <Loader size="sm" color={ACCENT} />
          <Text fz={14} className={classes.muted}>
            Đang tải block...
          </Text>
        </Group>
      </Box>
    );
  }

  if (!block) {
    return (
      <Box className={classes.page}>
        <BreadCrumb />
        <Stack gap={12} mt={16} align="flex-start">
          <Title order={1} fz={22}>
            {status === "error" ? "Không tải được danh sách block" : "Không tìm thấy block"}
          </Title>
          <Text fz={14} className={classes.muted}>
            {status === "error"
              ? "Kiểm tra kết nối hoặc đăng nhập lại rồi tải lại trang."
              : "Block có thể đã bị xoá."}
          </Text>
          <Button
            variant="default"
            onClick={() => navigate("/fixed-content/home-block")}
          >
            Về danh sách block
          </Button>
        </Stack>
      </Box>
    );
  }

  const info = templateInfo(block.template);

  return (
    <Box className={classes.page}>
      <BreadCrumb />
      <Stack gap={16} mt={8}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={8} miw={0} style={{ flex: "1 1 480px" }}>
            <Anchor
              component={Link}
              to="/fixed-content/home-block"
              fz={13}
              fw={600}
              underline="never"
              c="var(--hb-accent)"
              w="fit-content"
            >
              <Group gap={6}>
                <IconArrowLeft size={16} />
                Danh sách block
              </Group>
            </Anchor>
            <Title order={1} fz={24} lh={1.2}>
              {block.name}
            </Title>
            <Group gap={6}>
              <Badge variant="light" color="gray" radius="xl" tt="none" fw={600}>
                Mẫu: {info.name}
              </Badge>
              <Badge
                variant="light"
                color="gray"
                radius="xl"
                tt="none"
                fw={500}
                className={classes.mono}
              >
                {block.code}
              </Badge>
              <Badge variant="light" color="blue" radius="xl" tt="none" fw={600}>
                Vị trí {String(index + 1).padStart(2, "0")} trên trang
              </Badge>
              {unsaved && (
                <Badge variant="light" color="orange" radius="xl" tt="none" fw={600}>
                  Có thay đổi chưa lưu
                </Badge>
              )}
            </Group>
            {block.template === "hero" && (
              <Text fz={13} className={classes.muted} maw="70ch">
                Menu đầu trang và menu danh mục bên trái là phần cố định của
                web, không sửa ở block này.
              </Text>
            )}
          </Stack>
          <Group gap={8}>
            {/* Mở tab mới để không rời màn sửa, phần đang sửa chưa lưu vẫn còn */}
            <Button
              component={Link}
              to={`${GUIDE_PATH}/${block.template}`}
              target="_blank"
              rel="noopener"
              variant="subtle"
              color={ACCENT}
              leftSection={<IconBook2 size={16} />}
            >
              Hướng dẫn mẫu này
            </Button>
            <Button
              variant="default"
              disabled={saving}
              onClick={() => navigate("/fixed-content/home-block")}
            >
              Huỷ thay đổi
            </Button>
            <Button
              variant="filled"
              color={ACCENT}
              loading={saving}
              disabled={block.templateMissing}
              onClick={async () => {
                if (await saveBlock(block.id)) {
                  NotificationExtension.Success(`Đã lưu block ${block.name}`);
                }
              }}
            >
              Lưu block
            </Button>
          </Group>
        </Flex>

        <Tabs value={tab} onChange={setTab} color={ACCENT} keepMounted>
          <Tabs.List aria-label="Phần cấu hình">
            <Tabs.Tab value="content" fw={600}>
              Nội dung
            </Tabs.Tab>
            <Tabs.Tab value="display" fw={600}>
              Hiển thị và lịch
            </Tabs.Tab>
            <Tabs.Tab value="data" fw={600}>
              Dữ liệu lưu
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="content" pt={16}>
            {block.templateMissing ? (
              <Alert color="red" variant="light" radius="md" title="Chưa rõ mẫu của block">
                <Text fz={14}>
                  API chưa trả mẫu (TEMPLATE) của block {block.code} nên chưa mở trình sửa và chưa
                  cho lưu, để tránh ghi sai mẫu.
                </Text>
              </Alert>
            ) : (
              <BlockEditor block={block} />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="display" pt={16}>
            <Flex wrap="wrap" align="flex-start" gap={16}>
              <Box style={{ flex: "999 1 620px", minWidth: 0 }}>
                <DisplaySettings block={block} />
              </Box>
              <Paper
                withBorder
                radius="md"
                component="aside"
                aria-label="Ảnh thiết kế"
                style={{ flex: "1 1 340px", maxWidth: "100%" }}
              >
                <Text
                  component="h2"
                  fw={700}
                  fz={15}
                  m={0}
                  px={16}
                  py={14}
                  style={{ borderBottom: "1px solid var(--hb-border)" }}
                >
                  Ảnh thiết kế
                </Text>
                <Box p={16} bg="var(--hb-surface-2)">
                  <ZoomableImage imageId={block.thumbId} alt={`Ảnh thiết kế ${block.name}`} />
                </Box>
              </Paper>
            </Flex>
          </Tabs.Panel>
          <Tabs.Panel value="data" pt={16}>
            <SavedDataPanel block={block} order={index + 1} />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal
        opened={blocker.state === "blocked"}
        onClose={() => leaving === null && blocker.reset?.()}
        title={<Text fw={700}>Có thay đổi chưa lưu</Text>}
        centered
      >
        <Text fz={14}>
          Block "{block.name}" có thay đổi chưa lưu. Bạn có muốn lưu lại trước khi rời trang không?
        </Text>
        <Group justify="flex-end" gap={8} mt={20}>
          <Button variant="default" disabled={leaving !== null} onClick={() => blocker.reset?.()}>
            Ở lại
          </Button>
          <Button
            variant="subtle"
            color="red"
            loading={leaving === "discard"}
            disabled={leaving === "save"}
            onClick={() => leave("discard")}
          >
            Không lưu
          </Button>
          <Button
            color={ACCENT}
            loading={leaving === "save"}
            disabled={leaving === "discard" || block.templateMissing}
            onClick={() => leave("save")}
          >
            Lưu lại
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default HomeBlockEdit;
